import type { Question } from "@/schema/quizSchema";

type MultiQuestion = Extract<Question, { type: "multi" }>;

export function MultiChoice({
  question,
  value,
  onAnswer,
}: {
  question: MultiQuestion;
  value: string[] | undefined;
  onAnswer: (value: string[]) => void;
}) {
  const selected = value ?? [];
  const exclusiveValues = new Set(
    question.options.filter((o) => o.exclusive).map((o) => o.value),
  );
  // Enforce the schema's optional `max`: once the cap is reached, additional
  // options can't be selected, but already-selected ones stay deselectable.
  // Exclusive options (e.g. "None of these") don't count toward the cap.
  const atMax =
    question.max !== undefined &&
    selected.filter((s) => !exclusiveValues.has(s)).length >= question.max;
  const toggle = (v: string) => {
    if (selected.includes(v)) {
      onAnswer(selected.filter((s) => s !== v));
      return;
    }
    // Picking an exclusive option ("None of these") clears everything else.
    if (exclusiveValues.has(v)) {
      onAnswer([v]);
      return;
    }
    // Picking a normal option clears any selected exclusive.
    const kept = selected.filter((s) => !exclusiveValues.has(s));
    // `atMax` is computed from the pre-existing selection; recompute against
    // `kept` so dropping an exclusive frees a slot.
    if (question.max !== undefined && kept.length >= question.max) return;
    onAnswer([...kept, v]);
  };

  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
      <legend className="qk-prompt">{question.prompt}</legend>
      {question.helper && (
        <p className="qk-helper" data-qk-helper>
          {question.helper}
        </p>
      )}
      <div style={{ display: "grid", gap: "0.5rem" }}>
        {question.options.map((opt) => {
          const on = selected.includes(opt.value);
          // Cap reached and this option isn't already chosen → block adding it.
          const blocked = atMax && !on && !exclusiveValues.has(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              className="qk-option qk-option--multi"
              aria-pressed={on}
              aria-disabled={blocked || undefined}
              onClick={() => toggle(opt.value)}
            >
              {opt.emoji ? `${opt.emoji} ` : ""}
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
